/**
 * The session animation, replacing agent/internal/video.
 *
 * This runs FFmpeg — the real one, compiled to WebAssembly — with the same
 * command line the Go agent used. Not a reimplementation of it: the concat
 * demuxer, the scale+pad filter, libx264, yuv420p and the 0.5s-per-frame
 * timings are all literally the arguments from `video.go`, so the output is a
 * port rather than an approximation. The same choice `photobooth-pro-app` made.
 *
 *   0.5s per frame, the sequence repeated 4 times, 30fps, 1280x1280,
 *   letterboxed on white, yuv420p
 *
 * — timings straight from PrintWithLayout.GenerateGif().
 *
 * ── Why FFmpeg rather than WebCodecs ─────────────────────────────────────────
 *
 * An earlier version of this file encoded with WebCodecs and fell back to
 * FFmpeg. That is the faster path — a hardware H.264 encode takes a second or
 * two against tens of seconds here — but it made the output depend on the
 * platform's own encoder, which is exactly what a console shipping on both
 * Windows and macOS does not want:
 *
 *  - H.264 came from Media Foundation on Windows and VideoToolbox on macOS, so
 *    two event PCs produced measurably different files from the same session.
 *  - Either backend can be missing — acceleration disabled by policy, a
 *    virtualised GPU, a locked-down driver — and the failure is asynchronous,
 *    arriving on the `VideoEncoder` error callback rather than as a thrown
 *    exception. That needed a probe that encoded a real frame just to find out.
 *  - The canvas draw only approximated `scale=…:force_original_aspect_ratio=
 *    decrease, pad=…:color=white`, so mixed-orientation sessions were a
 *    lookalike of the agent's output rather than the same thing.
 *
 * FFmpeg has none of those properties. It is the same code on every machine, it
 * either loads or it does not, and it runs the filter the agent ran. What it
 * costs is a 32MB download and software encoding speed — see lib/ffmpeg.ts for
 * how that download is kept off the critical path.
 *
 * The file keeps the name `animate.mp4`, as it has since the desktop app called
 * it a GIF, because guests' links already point at it.
 */

import { encoderReady, loadFfmpeg } from '@/app/lib/ffmpeg'
import { concatScript } from '@/core/concat'
import { sanitizeFileName } from '@/core/session'

/** Timing lives in core/concat.ts, with the script it produces. */
const FRAME_RATE = 30

/** The square canvas every frame is padded into. 1280 is even; yuv420p needs that. */
export const CANVAS_SIZE = 1280

/**
 * The agent's filter, verbatim.
 *
 * Without it a session mixing portrait and landscape photos either fails
 * outright or produces corrupt video, because libx264 cannot change frame size
 * mid-stream and yuv420p rejects odd dimensions.
 */
const FILTER =
  `scale=${CANVAS_SIZE}:${CANVAS_SIZE}:force_original_aspect_ratio=decrease,` +
  `pad=${CANVAS_SIZE}:${CANVAS_SIZE}:(ow-iw)/2:(oh-ih)/2:color=white,setsar=1`

export interface VideoSupport {
  supported: boolean
  /** True when the encoder still has to be downloaded before a session can use it. */
  needsDownload: boolean
  /** Why it is unavailable, in Indonesian, for the banner and step 1. */
  reason?: string
}

/**
 * Whether this machine can encode at all.
 *
 * Cheap and synchronous-ish by design: there is no encoder to interrogate any
 * more, only WebAssembly to have. The old version had to configure a real
 * VideoEncoder and push a frame through it because `isConfigSupported()` could
 * not be trusted; nothing here varies by platform, so nothing has to be probed.
 */
export async function probeVideoSupport(): Promise<VideoSupport> {
  if (typeof WebAssembly === 'undefined') {
    return {
      supported: false,
      needsDownload: false,
      reason: 'Browser ini tidak mendukung WebAssembly',
    }
  }
  return { supported: true, needsDownload: !(await encoderReady()) }
}

/**
 * Encodes the session's photos into `animate.mp4`.
 *
 * `onProgress` reports 0..1. This runs after the sheet has printed and the
 * guest has walked away, so it is allowed to be slow — but it must never be on
 * the path between pressing Cetak and paper coming out.
 */
export async function encodeSession(
  photos: { name: string; blob: Blob }[],
  onProgress?: (fraction: number) => void,
): Promise<Blob> {
  if (photos.length === 0) throw new Error('Tidak ada foto untuk dianimasikan')

  const ffmpeg = await loadFfmpeg()

  const list = 'concat.txt'
  const output = 'animate.mp4'
  // Numbered rather than named after the photo: the concat demuxer resolves
  // entries relative to the script, and a camera file name can carry anything.
  const names = photos.map((photo, index) => {
    const extension = sanitizeFileName(photo.name).split('.').pop() ?? 'jpg'
    return `f${index}.${extension.toLowerCase()}`
  })

  const written: string[] = []
  const report = ({ progress }: { progress: number }) => {
    // FFmpeg reports slightly over 1 near the end; clamp so a caller can trust
    // the range.
    onProgress?.(Math.max(0, Math.min(1, progress)))
  }

  try {
    for (const [index, photo] of photos.entries()) {
      const name = names[index]!
      await ffmpeg.writeFile(name, new Uint8Array(await photo.blob.arrayBuffer()))
      written.push(name)
    }

    await ffmpeg.writeFile(list, concatScript(names))
    written.push(list)

    ffmpeg.on('progress', report)

    const code = await ffmpeg.exec([
      '-y',
      '-f',
      'concat',
      '-safe',
      '0',
      '-i',
      list,
      '-vf',
      FILTER,
      '-c:v',
      'libx264',
      '-pix_fmt',
      'yuv420p',
      '-preset',
      'veryfast',
      '-r',
      String(FRAME_RATE),
      // Not in the agent's command: it wrote to a folder Drive synced, where
      // nothing streamed the file. These are opened from a phone over a guest's
      // mobile connection, so the moov atom belongs at the front.
      '-movflags',
      '+faststart',
      output,
    ])
    if (code !== 0) throw new Error(`FFmpeg keluar dengan kode ${code}`)

    const data = (await ffmpeg.readFile(output)) as Uint8Array
    written.push(output)
    onProgress?.(1)
    return new Blob([data as BlobPart], { type: 'video/mp4' })
  } finally {
    ffmpeg.off('progress', report)
    // The wasm heap is not reclaimed on its own, and an evening of sessions
    // would otherwise accumulate every photo of every guest in it.
    for (const name of written) {
      await ffmpeg.deleteFile(name).catch(() => undefined)
    }
  }
}
